import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';

import { DEBOUNCE_TIMEOUT } from 'utils/constants';
import InputField from 'shared/components/forms/InputField/InputField';

const TextInput = ({
  register,
  name,
  disabled = false,
  type = 'text',
  label = null,
  getFieldError,
  required = false,
  onChange,
  ...props
}) => {
  const field = register(name, { required, disabled });

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const debounced = useMemo(
    () =>
      debounce((payload) => {
        onChangeRef.current?.(payload.name, payload.type, payload.value);
      }, DEBOUNCE_TIMEOUT),
    []
  );

  useEffect(() => () => debounced.cancel(), [debounced]);

  const handleChange = (e) => {
    field.onChange(e);
    debounced({ name: field.name, type, value: e.target.value });
  };

  const handleBlur = (e) => {
    debounced.flush();
    field.onBlur(e);
  };

  return (
    <InputField
      {...field}
      {...props}
      type={type}
      label={label}
      required={required}
      disabled={disabled}
      error={getFieldError(name)}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

TextInput.propTypes = {
  register: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  type: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  getFieldError: PropTypes.func.isRequired,
};

export default TextInput;
